import {useCallback} from 'react';
import {useStoreApi, internalsSymbol} from 'reactflow';

export interface HandleData {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export type GetHandlesFunc = (nodeId: string) => Array<HandleData>;

/**
 * Use ReactFlow's internal API to get a function for retrieving handles data for a given node.
 *
 * @returns A function that, given a node ID, returns the ReactFlow internal handles data for that node.
 */
export function useGetNodeHandlesData(): GetHandlesFunc {
  // Currently, this is our best way for getting detailed handle information from ReactFlow. At time of
  // writing, we are on ReactFlow version 11.0.6. In ReactFlow version 12 and higher, the 'nodeInternals'
  // property below is changed to `nodeLookup`. See here: https://reactflow.dev/learn/troubleshooting/migrate-to-v12#11-rename-nodeinternals
  const store = useStoreApi();
  return useCallback(
    (nodeId: string) => {
      const {nodeInternals} = store.getState();
      const hb = nodeInternals.get(nodeId)?.[internalsSymbol]?.handleBounds;
      return [...(hb?.source ?? []), ...(hb?.target ?? [])].map((handle) => ({
        id: handle.id ?? '',
        x: handle.x,
        y: handle.y,
        width: handle.width,
        height: handle.height,
      }));
    },
    [store]
  );
}
